'use client';

import { Fragment, type ReactNode } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import { cn } from '@/lib/utils';

interface HighlightMatchProps {
  text: string | null | undefined;
  query: string | string[];
  className?: string;
}

interface MatchRange {
  start: number;
  end: number;
}

function buildRanges(haystack: string, needles: string[]): MatchRange[] {
  const lower = haystack.toLowerCase();
  const ranges: MatchRange[] = [];

  for (const raw of needles) {
    const needle = raw.trim().toLowerCase();
    if (!needle) continue;

    let from = 0;
    let idx = lower.indexOf(needle, from);
    while (idx !== -1) {
      ranges.push({ start: idx, end: idx + needle.length });
      from = idx + needle.length;
      idx = lower.indexOf(needle, from);
    }
  }

  if (ranges.length === 0) return ranges;

  ranges.sort((a, b) => a.start - b.start || a.end - b.end);

  const merged: MatchRange[] = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    const next = ranges[i];
    if (next.start <= last.end) {
      last.end = Math.max(last.end, next.end);
    } else {
      merged.push(next);
    }
  }
  return merged;
}

export function HighlightMatch({
  text,
  query,
  className
}: HighlightMatchProps) {
  if (!text) return null;
  const needles = Array.isArray(query) ? query : [query];
  if (needles.length === 0) return <>{text}</>;

  const ranges = buildRanges(text, needles);
  if (ranges.length === 0) return <>{text}</>;

  const parts: ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < ranges.length; i++) {
    const { start, end } = ranges[i];
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <mark
        key={`m-${start}`}
        className={cn(
          'rounded-sm bg-yellow-300/70 px-0.5 text-inherit dark:bg-yellow-600/40',
          className
        )}
      >
        {text.slice(start, end)}
      </mark>
    );
    cursor = end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </>
  );
}

/**
 * Normalize a TanStack column filter value (set by DataTableChipsFilter)
 * into a `string[]` of search terms — empty when the column has no filter.
 */
export function getColumnSearchTerms<TData extends RowData>(
  column: Column<TData, unknown>
): string[] {
  const raw = column.getFilterValue();
  if (Array.isArray(raw)) {
    return raw.filter(
      (v): v is string => typeof v === 'string' && v.length > 0
    );
  }
  if (typeof raw === 'string' && raw.length > 0) return [raw];
  return [];
}
